import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FormArray, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {
  AddChipTime,
  CompetitorSaveControlPointResult,
  CompetitorSaveControlPointResultStatus,
  CompetitorStatus,
  ControlPointTime,
  EventItem,
  Lookups,
  MeasurementTimeItemHolder,
  Race,
  RaceSettings,
  ResultProcessingType,
  Waypoint,
  WaypointType,
  TOAST_STATE
} from '@model';
import {TranslocoService} from '@ngneat/transloco';
import {getLanguageValue} from '@utility';
import {debounceTime, distinctUntilChanged, finalize} from 'rxjs';
import {
  CompetitorService,
  LoaderService,
  MeasurementsService,
  MeasurementTimeItemHolderService,
  ToastService
} from '@services';
import {AbstractComponent, AddChipTimeModalComponent, ConfirmationModalComponent} from "@components";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {v4 as uuidv4} from 'uuid';
import {ErrorMessagesPipe, LocalizedDatePipe, StartedRacesPipe} from "@pipes";

@Component({
  selector: 'app-event-measurements-points-laps',
  templateUrl: './event-measurements-points-laps.component.html',
  styleUrl: './event-measurements-points-laps.component.scss'
})
export class EventMeasurementsPointsLapsComponent extends AbstractComponent implements OnInit{
  @Input() measurementsPointsView = true;
  @Input() eventItem?: EventItem;
  @Input() waypointsList: Waypoint[] = [];
  @Input() allWaypointList: Waypoint[] = [];
  @Input({required: true}) languages: Lookups[] = [];
  @Output() notifyMeasurementTabActive = new EventEmitter<void>();
  @Output() refreshCounters = new EventEmitter<void>();

  waypoints: Waypoint[] = [];
  waypointIds: number[] = [];
  waypointLabels: string[] = [];
  raceListIds: number[] = [];
  raceListLabels: string[] = [];
  raceList: Race[] = [];
  measurementTimeItemHolders: MeasurementTimeItemHolder[] = [];
  competitorControlPointTimeItemsErrors: string[] = [];
  isLoading: boolean = false;
  showLapDistancePassedColumn = false;

  selectedRace: Race | undefined;

  inputForm = this.formBuilder.group({
    competitorControlPointTimeItems: this.formBuilder.array([]),
    controlPointType: new FormControl(),
    raceId: new FormControl(-1)
  });


  constructor(
    public formBuilder: FormBuilder,
    private translocoService: TranslocoService,
    private competitorService: CompetitorService,
    private measurementService: MeasurementsService,
    private loaderService: LoaderService,
    private toastService: ToastService,
    private modalService: NgbModal,
    private localizedDatePipe: LocalizedDatePipe,
    private errorMessagesPipe: ErrorMessagesPipe,
    private measurementTimeItemHolderService: MeasurementTimeItemHolderService,
  ) {
    super();

  }

  ngOnInit(): void {
    this.filterRaceList()
    this.initWaypoints();
    this.initRaceFormControls();
    this.getDataFromLocalStorage(true);
    this.inputForm.controls.competitorControlPointTimeItems.valueChanges.pipe(
        debounceTime(500),
        distinctUntilChanged()
    ).subscribe(this.updateMeasurementTimeItemHolders);
    this.subscriptions.push(this.inputForm.controls.raceId.valueChanges.subscribe(val => {
      if (val && val > -1) {
        this.selectedRace = this.raceList.find(r => r.id === val);
      }
      else {
        this.selectedRace = undefined;
      }
    }));
    if (this.measurementsPointsView) {
      this.notifyMeasurementTabActive.emit();
    }
  }


  private getDataFromLocalStorage = (update?: boolean) => {
    this.isLoading = !!update;
    this.measurementTimeItemHolderService.getHolderForEvent(this.measurementsPointsView, this.eventItem!.id!, update).subscribe(holder => {
      const measurementTimeItemHolders: MeasurementTimeItemHolder[] = holder;
      if (measurementTimeItemHolders?.length) {
        this.measurementTimeItemHolders = measurementTimeItemHolders;
        this.competitorControlPointTimeItems.clear();
        this.measurementTimeItemHolders.forEach(item => this.addMeasurementTimeItem(item, false));
      }
      if (!this.measurementsPointsView) {
        const startNumbers = Array.from(
            new Set(measurementTimeItemHolders
                .filter(holder => holder.startNumber)
                .map(holder => holder.startNumber))
        );
        startNumbers.forEach(startNumber => this.saveData(startNumber))
      }
      this.isLoading = false;
    })
  }

  private filterRaceList = () => {
    this.raceList = (this.eventItem?.racesList ?? []).filter(item =>
      this.measurementsPointsView ? !(RaceSettings.isRoundRace(item.raceSettings?.minimumLapDuration) || Race.isBackyardUltra(item.sport)) : (RaceSettings.isRoundRace(item.raceSettings?.minimumLapDuration) || Race.isBackyardUltra(item.sport))
    );
  }

  private initWaypointFormControls = () => {
    this.waypointIds = this.waypoints.map(item => item.id!);
    this.waypointLabels = this.waypoints.map(item => {
      const typeTranslation = this.translocoService.translate(`organiser.event.details.control.point.waypointType.${item.waypointTypeId}`);
      return item.waypointTypeId === WaypointType.CONTROL_WAYPOINT ? `${typeTranslation} ${getLanguageValue(item.pointNameHr, item.pointNameEn, item.pointNameIt, item.pointNameSlo, item.pointNameSr, item.pointNameBs, this.translocoService.getActiveLang()) ?? '-'}` : typeTranslation;
    });
    this.waypointIds.length && this.inputForm.controls.controlPointType.setValue(this.waypointIds[this.waypointIds.length - 1]);
  }

  private initRaceFormControls = () => {
    this.raceListIds = [this.ALL];
    this.raceListIds.push(...this.raceList.map(item => item.id!));
    this.raceListLabels = [this.translocoService.translate("measurements.measurement.races.all")];
    this.raceListLabels.push(...this.raceList.map(item => getLanguageValue(item.name, item.nameEng, item.nameIta, item.nameSlo,item.nameSr,item.nameBs, this.translocoService.getActiveLang()) ?? ''));
  }

  get competitorControlPointTimeItems(): FormArray<FormGroup> {
    return this.inputForm.controls.competitorControlPointTimeItems as FormArray;
  }

  private initWaypoints = () => {
    this.waypoints = [...this.waypointsList];
    this.initWaypointFormControls();
  }

  public addMeasurementTimeItem = (item?: MeasurementTimeItemHolder, fromUi: boolean = true) => {
    const formGroup = this.formBuilder.group({
      time: new FormControl(item?.time ?? new Date()),
      penaltyMinutes: new FormControl(item?.penaltyMinutes ?? 0),
      lapDistancePassed: new FormControl(item?.lapDistancePassed),
      status: new FormControl(item?.status ?? CompetitorStatus.FIN),
      checkpoints: new FormControl(item?.checkpoints),
      startNumber: new FormControl(item?.startNumber),
      waypointId: new FormControl(item?.waypointId ?? (this.measurementsPointsView && this.waypointIds.length ? this.inputForm.controls.controlPointType.value : null)),
      raceId: new FormControl(item?.competitor?.raceId ?? (item?.raceId ?? this.inputForm.controls.raceId.value))
    });
    if (fromUi) {
      this.measurementTimeItemHolders.push(this.createMeasurementTimeItemHolder(formGroup));
    }
    this.handleLapPassedDistanceControl(formGroup, item?.competitor?.id ? formGroup.get("raceId")?.value ?? undefined : undefined);

    this.competitorControlPointTimeItems.push(formGroup);
    this.competitorControlPointTimeItemsErrors.push(this.getErrorMessage(formGroup));
    let index = this.competitorControlPointTimeItems.controls.length-1;
    this.competitorControlPointTimeItems.at(index).valueChanges.subscribe(_value => {
      this.competitorControlPointTimeItemsErrors[index] = this.getErrorMessage(this.competitorControlPointTimeItems.at(index))
    })
    this.inputForm.updateValueAndValidity();
  }

  private createMeasurementTimeItemHolder = (formGroup: FormGroup): MeasurementTimeItemHolder => ({
    time: formGroup.get('time')?.value,
    penaltyMinutes: formGroup.get('penaltyMinutes')?.value,
    lapDistancePassed: formGroup.get('lapDistancePassed')?.value,
    status: formGroup.get('status')?.value,
    startNumber: formGroup.get('startNumber')?.value,
    waypointId: formGroup.get('waypointId')?.value,
    raceId: formGroup.get('raceId')?.value,
    checkpoints: formGroup.get('checkpoints')?.value,
    key: uuidv4(),
    saveResult: CompetitorSaveControlPointResultStatus.OK
  });

  public onDeleteMeasurementItem = (index: number) => {
    const modalRef = this.modalService.open(ConfirmationModalComponent, {
      centered: true,
    });
    modalRef.componentInstance.title = this.translocoService.translate("measurements.controlpoints.delete.dialog.title");
    modalRef.componentInstance.text = this.translocoService.translate("measurements.controlpoints.delete.dialog.text");

      modalRef.result.then((_) => {
      this.deleteMeasurementItem(index);
    }, (_) => {
      /// ignore
    });
  }

  public onDeleteAllMeasurementItem = () => {
    const modalRef = this.modalService.open(ConfirmationModalComponent, {
      centered: true,
    });
    modalRef.componentInstance.title = this.translocoService.translate("measurements.controlpoints.delete.all.dialog.title");
    modalRef.componentInstance.text = this.translocoService.translate("measurements.controlpoints.delete.all.dialog.text");

      modalRef.result.then((_) => {
        this.measurementTimeItemHolders.forEach(holder => holder.subscription?.unsubscribe());
        this.measurementTimeItemHolders = [];
        this.competitorControlPointTimeItems.clear();
        this.saveData();
        this.inputForm.updateValueAndValidity();
        }, (_) => {
      /// ignore
    });
  }

  public deleteMeasurementItem = (index: number) => {
    this.measurementTimeItemHolders[index].subscription?.unsubscribe();
    if (!this.measurementsPointsView) {
      const {startNumber} = this.measurementTimeItemHolders[index];
      this.measurementTimeItemHolders.splice(index, 1);
      if (startNumber) {
        this.saveData(startNumber);
      }
    } else {
      this.measurementTimeItemHolders.splice(index, 1);
    }
    this.competitorControlPointTimeItems.removeAt(index);
    if (index == 0) {
      this.saveData();
    }
    this.inputForm.updateValueAndValidity();
  }


  private updateMeasurementTimeItemHolders = (value: any) => {
    value.forEach((item:any, index:number) => {
      let assignLaps : boolean = false;
      if (item.startNumber && (this.measurementTimeItemHolders[index].startNumber !== item.startNumber || this.measurementTimeItemHolders[index].raceId !== item.raceId || this.measurementTimeItemHolders[index].waypointId !== item.waypointId)) {
        if (!this.measurementsPointsView && this.measurementTimeItemHolders[index].startNumber !== item.startNumber) {
          //update other laps if start number exists
          const changedStartNumber = this.measurementTimeItemHolders[index].startNumber;
          if (changedStartNumber) {
            const firstIndex = this.measurementTimeItemHolders.findIndex(
                (item, i) => i !== index && item.startNumber === changedStartNumber
            );
            if (firstIndex !== -1) {
              this.onRefreshMeasurementItem(firstIndex, true)
            }
          }
        }
        this.measurementTimeItemHolders[index].subscription = this.competitorService.fetchCompetitor(item.startNumber, item.raceId !== -1 ? item.raceId : undefined, this.eventItem?.id, item.waypointId, !this.measurementsPointsView).subscribe(competitor => {
          this.measurementTimeItemHolders[index].competitor = competitor;
          if (competitor?.id) { /// competitor is unrecognized
            this.competitorControlPointTimeItems.at(index).get('raceId')?.setValue(competitor!.raceId, {onlySelf: true, emitEvent: false});
          }
          if (this.measurementsPointsView) {
            this.saveData();
          } else {
            if (competitor?.id && item.raceId != -1 && item.raceId) {
              this.saveData(item.startNumber);
            }
            else {
              this.measurementTimeItemHolders[index].lap = undefined;
              this.saveData()
            }
          }
          this.handleLapPassedDistanceControl(this.competitorControlPointTimeItems.at(index), competitor?.id ? competitor?.raceId : undefined);
          this.competitorControlPointTimeItems.at(index).updateValueAndValidity();
        });
      }
      //time changed
      else if (item.startNumber && item.time !== this.measurementTimeItemHolders[index].time && this.measurementTimeItemHolders[index].competitor && !this.measurementsPointsView) {
        assignLaps = true;
      }

      if (!item.startNumber) {
        this.measurementTimeItemHolders[index].competitor = undefined;
        this.measurementTimeItemHolders[index].lap = undefined;
        this.handleLapPassedDistanceControl(this.competitorControlPointTimeItems.at(index), undefined);
      }

      if (!this.equalUIValues(this.measurementTimeItemHolders[index], item)) {
        item.saveResult = CompetitorSaveControlPointResultStatus.OK;
      }

      this.measurementTimeItemHolders[index] = { ...this.measurementTimeItemHolders[index], ...item };
      assignLaps ? this.saveData(item.startNumber): this.saveData();
    });
  }

  equalUIValues(a: MeasurementTimeItemHolder, b: MeasurementTimeItemHolder) {
    return a.time === b.time && a.startNumber === b.startNumber && a.raceId === b.raceId &&
        ((!a.lapDistancePassed && !b.lapDistancePassed ) ||
            a.lapDistancePassed === b.lapDistancePassed)
    && a.checkpoints === b.checkpoints && a.penaltyMinutes === b.penaltyMinutes && a.status === b.status && a.waypointId === b.waypointId;
  }

  getErrorMessage(formGroup: any): string {
      if (formGroup instanceof FormGroup) {
          for (const controlName of Object.keys(formGroup.controls)) {
            const control = formGroup.get(controlName) as FormControl;
            if (control?.errors) {
              const errorMessage = this.errorMessagesPipe.transform(control.errors, controlName)?.at(0);
              if (errorMessage) {
                return errorMessage;
              }
            }
          }
      }
      return "";
  }

  onRefreshMeasurementItem(index:number, hideLoading?: boolean) {
    if (this.measurementTimeItemHolders[index].startNumber) {
      if (!hideLoading) {
        this.loaderService.startLoading();
      }
      this.measurementTimeItemHolders[index].subscription = this.competitorService.fetchCompetitor(this.measurementTimeItemHolders[index].startNumber!, this.measurementTimeItemHolders[index].raceId, this.eventItem?.id, this.measurementTimeItemHolders[index].waypointId).pipe(finalize(() => {
        this.loaderService.dismissLoading();
      })).subscribe(competitor => {
        if (!competitor) {
          this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("measurements.unrecognized.competitor"));
        }
        this.measurementTimeItemHolders[index].competitor = competitor;
        if (competitor?.id) {
          this.competitorControlPointTimeItems.at(index).get('raceId')?.setValue(competitor!.raceId);
        }
        if (this.measurementsPointsView) {
          this.saveData();
        } else if (competitor?.id) {
          this.saveData(this.measurementTimeItemHolders[index].startNumber!);
        } else {
          if (!hideLoading) {
            this.loaderService.dismissLoading();
          }
          this.saveData();
        }
        this.handleLapPassedDistanceControl(this.competitorControlPointTimeItems.at(index), competitor?.id ? competitor?.raceId : undefined);
      });
    }
  }

  onRefreshAllMeasurementItem() {
    for (let i: number = 0; i < this.measurementTimeItemHolders.length; i++) {
      this.onRefreshMeasurementItem(i, true);
    }
  }


  assignLapsToCompetitors(startNumber: string) {
    const indexedItems = this.measurementTimeItemHolders.map((item, index) => ({
          index,
          time: item.time!,
          startNumber: item.startNumber,
          competitor: item.competitor
        })).filter(item => item.time && item.startNumber === startNumber && item.competitor);

    if (indexedItems.length === 0) return;

    const competitor = indexedItems[0].competitor;
    let savedCompetitorLaps: ControlPointTime[] = competitor?.laps ? [...competitor.laps] : [];

    const savedItems = savedCompetitorLaps
        .filter(lap => !!lap.time)
        .map((item) => ({ index: -1, time: item.time! }));

    const minimumLapDuration: number | undefined =
        Number(this.raceList.find(race => race.id === competitor?.raceId)?.raceSettings?.minimumLapDuration) || undefined;

    const allItems = [...savedItems, ...indexedItems];
    allItems.sort((a, b) => a.time.getTime() - b.time.getTime());

    allItems.forEach((h,i) => {
      if (h.index !== -1) {
        const holder: MeasurementTimeItemHolder =  this.measurementTimeItemHolders[h.index];
        const lap = i + 1;
        holder.lap = lap;
        const previousTime = i === 0 ? holder.competitor?.startTime : allItems[i-1].time;
        const timeDifference = (previousTime && holder.time) ? (holder.time?.getTime() - previousTime.getTime()) / 1000 : undefined;
        const timeDifferenceError = timeDifference && previousTime && minimumLapDuration && timeDifference < minimumLapDuration;
        holder.saveResult = timeDifferenceError ? CompetitorSaveControlPointResultStatus.MINIMAL_LAP_DURATION_ERROR :
            holder.saveResult !== CompetitorSaveControlPointResultStatus.MINIMAL_LAP_DURATION_ERROR ? holder.saveResult : CompetitorSaveControlPointResultStatus.OK;
        holder.errorMessage = timeDifferenceError ? this.getMinimalLapErrorMessage(lap, lap-1, minimumLapDuration!, previousTime!, i === 0) : undefined;
        this.competitorControlPointTimeItems
            .at(h.index)
            .get('lap')
            ?.setValue(lap, {onlySelf: true, emitEvent: false});
      }
    });
  }


  getMinimalLapErrorMessage(lap: number, previousLap: number, minimumLapDuration: number, time: Date | undefined, isStart: boolean = false): string {
    const errorMessage1 = this.translocoService.translate('measurements.competitor.control.point.save.error.minimal.lap.error.1', {
      lap: lap,
      seconds: minimumLapDuration,
    });

    const errorMessageLap = !isStart ? this.translocoService.translate('measurements.competitor.control.point.save.error.minimal.lap.error.lap', {
      lap: previousLap,
    }) : this.translocoService.translate('measurements.competitor.control.point.save.error.minimal.lap.error.start');

    const errorMessage2 = this.translocoService.translate('measurements.competitor.control.point.save.error.minimal.lap.error.2', {
      time: this.localizedDatePipe.transform(time, 'dd.MM.yyyy HH:mm:ss.SSS'),
    });

    const fullErrorMessage = `${errorMessage1} ${errorMessageLap} ${errorMessage2}`;

    return fullErrorMessage;
  }

  saveData(startNumber?:string) {
    if (startNumber) {
      this.assignLapsToCompetitors(startNumber)
    }
    if (this.eventItem?.id) {
      this.measurementTimeItemHolderService.saveHolderForEvent(this.measurementsPointsView, this.eventItem!.id!, JSON.stringify(this.measurementTimeItemHolders.map(item => ({...item, subscription: undefined}))))
    }
  }
  isSaveToServerDisabled = () => this.measurementTimeItemHolders.length === 0 || this.measurementTimeItemHolders.every((item: any) => !item.startNumber || !item.competitor || !item.competitor.startTime) || this.competitorControlPointTimeItems.invalid;
  saveToServer = () => {
    let competitorsToSave = this.measurementTimeItemHolders.filter(item=>item.startNumber && item.competitor && item.competitor.startTime && item.saveResult === CompetitorSaveControlPointResultStatus.OK).map(item => {
      let competitor = {...item.competitor};
      competitor.controlPointsTimes = [{
        time: item.time,
        penaltyMinutes: item.penaltyMinutes,
        lapDistancePassed: item.lapDistancePassed,
        checkpoints: item.checkpoints,
        controlWaypoint: this.waypoints.find(waypoint => waypoint.id === item.waypointId),
        lap: item.lap,
        key: item.key,
      }];
      competitor.status = item.status;
      return competitor;
    });
    let competitorsToLeave = this.measurementTimeItemHolders.filter(item=>!(item.startNumber && item.competitor && item.competitor.startTime && item.saveResult === CompetitorSaveControlPointResultStatus.OK));
    if (competitorsToSave.length === 0) {
      this.toastService.showToast(TOAST_STATE.warning, this.translocoService.translate("measurements.controlpoints.savetoserver.warning"));
      return;
    }
    const handleSaveSuccess = (competitorResults: CompetitorSaveControlPointResult[]) => {
      this.loaderService.dismissLoading();
      this.toastService.showToast(TOAST_STATE.success, this.translocoService.translate(this.measurementsPointsView ? "measurements.controlpoints.savetoserver.success" : "measurements.laps.savetoserver.success"));

      const items: MeasurementTimeItemHolder[] = []
      competitorResults.forEach(competitorResult => {
        const holder = this.measurementTimeItemHolders.find(item => item.key === competitorResult.key);
        if (holder) {
          holder.competitor = competitorResult.competitor;
          holder.saveResult = competitorResult.status;
          if (competitorResult.status !== CompetitorSaveControlPointResultStatus.OK) {
            items.push(holder)
          }
        }
      });

      this.measurementTimeItemHolders = [...items, ...competitorsToLeave];
      if (this.measurementTimeItemHolders.length === 0) {
        this.measurementTimeItemHolders = [];
        this.competitorControlPointTimeItems.clear();
        this.measurementTimeItemHolderService.deleteHolderForEvent(this.measurementsPointsView, this.eventItem!.id!)
      } else {
        this.measurementTimeItemHolderService.saveHolderForEvent(this.measurementsPointsView, this.eventItem!.id!, JSON.stringify(this.measurementTimeItemHolders.map(item => ({...item, subscription: undefined}))))
        this.measurementTimeItemHolders = [];
        this.competitorControlPointTimeItems.clear();
        this.getDataFromLocalStorage();
      }

      this.refreshCounters.emit();
      this.inputForm.updateValueAndValidity();
    };

    const handleSaveError = () => {
      this.loaderService.dismissLoading();
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate(this.measurementsPointsView ? "measurements.controlpoints.savetoserver.error" : "measurements.laps.savetoserver.error"));
    };

    this.loaderService.startLoading();
    this.measurementsPointsView
      ? this.measurementService.saveCompetitorsControlPoints(competitorsToSave, this.eventItem!.id!, handleSaveSuccess, handleSaveError)
      : this.measurementService.saveOrUpdateCompetitorsLaps(competitorsToSave, this.eventItem!.id!, handleSaveSuccess, handleSaveError);
  }

  getWaypointType(waypointId: number): WaypointType | undefined {
    return this.waypoints.find(waypoint => waypoint.id === waypointId)?.waypointTypeId;
  }

  addChipTime() {
    const waypointsList = this.measurementsPointsView ? this.allWaypointList : this.allWaypointList.filter(item => item.waypointTypeId === WaypointType.START_WAYPOINT);
    let waypointIds = waypointsList.map(item => item.id!);
    let waypointLabels = waypointsList.map(item => {
      const typeTranslation = this.translocoService.translate(`organiser.event.details.control.point.waypointType.${item.waypointTypeId}`);
      return item.waypointTypeId === WaypointType.CONTROL_WAYPOINT ? `${typeTranslation} ${getLanguageValue(item.pointNameHr, item.pointNameEn, item.pointNameIt, item.pointNameSlo, item.pointNameSr, item.pointNameBs, this.translocoService.getActiveLang()) ?? '-'}` : typeTranslation;
    });

    if (this.measurementsPointsView === false) {
      waypointIds = [-1, ...waypointIds];
      waypointLabels = [this.translocoService.translate("measurements.controlpoints.lap"), ...waypointLabels];
    }

    const modalRef = this.modalService.open(AddChipTimeModalComponent, {
      centered: true,
      windowClass: 'chip-time-modal'
    });
    modalRef.componentInstance.title = this.translocoService.translate("measurements.controlpoints.button.add.chip.time");
    modalRef.componentInstance.waypointIds = waypointIds;
    modalRef.componentInstance.waypointLabels = waypointLabels;
    modalRef.componentInstance.raceListIds = this.raceListIds;
    modalRef.componentInstance.raceListLabels = this.raceListLabels;
    modalRef.componentInstance.measurementsPointsView = this.measurementsPointsView;
    modalRef.componentInstance.languages = this.languages;
    modalRef.componentInstance.event = this.eventItem;
    this.subscriptions.push(modalRef.componentInstance.onSave.subscribe((addChipTime: AddChipTime) => {
      this.onAddChipTime(addChipTime);
    }))
  }

  onAddChipTime(addChipTime: AddChipTime): void {
    this.loaderService.startLoading();
    addChipTime.eventId = this.eventItem!.id!;
    if (addChipTime.raceId === this.ALL) {
      addChipTime.raceId = undefined;
    }
    this.measurementService.addChipTime(addChipTime, !this.measurementsPointsView, (competitorSaveControlPointResults: CompetitorSaveControlPointResult[])=>{
      this.loaderService.dismissLoading();
      if (competitorSaveControlPointResults.every(result => result.status === CompetitorSaveControlPointResultStatus.OK)) {
        this.toastService.showToast(TOAST_STATE.success, this.translocoService.translate(this.measurementsPointsView ? "measurements.controlpoints.savetoserver.success" : "measurements.laps.savetoserver.success"));
      } else {
        let toastState = TOAST_STATE.success;
        let resultMessage = "";
        if (competitorSaveControlPointResults.every(result => result.status !== CompetitorSaveControlPointResultStatus.OK)) {
          toastState = TOAST_STATE.danger;
          resultMessage = this.translocoService.translate(this.measurementsPointsView ? "measurements.controlpoints.savetoserver.error" : "measurements.laps.savetoserver.error") + "</br>";
        } else {
          resultMessage = this.translocoService.translate(this.measurementsPointsView ? "measurements.controlpoints.savetoserver.success" : "measurements.laps.savetoserver.success") + "</br>";
        }
        Object.values(CompetitorSaveControlPointResultStatus).forEach(value => {
          if (value !== CompetitorSaveControlPointResultStatus.OK) {
            const chipNumbers = this.findChipNumbersByStatus(competitorSaveControlPointResults, value);
            if ((chipNumbers.length ?? 0) > 0) {
              const uniqueChipNumbers = [...new Set(chipNumbers)];
              resultMessage += "</br>" + this.translocoService.translate(`measurements.competitor.control.point.save.error.${value}`) + " : " + "(" + uniqueChipNumbers.join(', ') + ")";
            }
          }
        });
        this.toastService.showToast(toastState, resultMessage);
      }
      this.refreshCounters.emit();
    }, ()=>{
      this.loaderService.dismissLoading();
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate(this.measurementsPointsView ? "measurements.controlpoints.chip.savetoserver.error" : "measurements.laps.chip.savetoserver.error"));
    });
  }

  handleLapPassedDistanceControl(formGroup: FormGroup, raceId: number | undefined) {
    const raceSettings: RaceSettings | undefined = raceId ?
        this.raceList.find(race => race.id === raceId)?.raceSettings || undefined : undefined;
    if (raceSettings?.resultProcessingType === ResultProcessingType.TIME_LIMITED && raceSettings.lapLength && formGroup.get('startNumber')?.value) {
      formGroup.get('lapDistancePassed')?.enable({ emitEvent: false });
      formGroup.get('lapDistancePassed')?.setValidators([Validators.max(raceSettings.lapLength!)])
      this.showLapDistancePassedColumn = true;
    }
    else {
      formGroup.get('lapDistancePassed')?.setValue(null, {onlySelf: true, emitEvent: false});
      formGroup.get('lapDistancePassed')?.disable({ emitEvent: false });
      this.showLapDistancePassedColumn = this.competitorControlPointTimeItems.controls.some(c => !!c.get('lapDistancePassed')?.enabled);
    }
  }

  findChipNumbersByStatus(
    results: CompetitorSaveControlPointResult[],
    status: CompetitorSaveControlPointResultStatus
  ): string[] {
    return results
      .filter(result => result.status === status)
      .map(result => result.competitor?.chipNumber)
      .filter(chipNumber => chipNumber !== undefined) as string[];
  }

  protected readonly WaypointType = WaypointType;
  protected readonly CompetitorSaveControlPointResultStatus = CompetitorSaveControlPointResultStatus;
  private readonly ALL = -1;
}

